import { IBenefitItem } from "../../interfaces/benefit";
import { formatDateParts, formatPhone } from "../../utils/format";
import { AlertIcon, InboxIcon } from "../ui/Icons";
import AnswerPill from "./AnswerPill";

interface IBenefitsTableProps {
  benefits: IBenefitItem[];
  isLoading: boolean;
  error: string | null;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

const COLUMNS: string[] = [
  "RUT",
  "Nombre",
  "Teléfono",
  "Fecha Carga",
  "Fecha Envío",
  "Fecha Respuesta",
  "Respuesta",
  "Archivo",
];

export default function BenefitsTable({
  benefits,
  isLoading,
  error,
  hasActiveFilters,
  onClearFilters,
}: IBenefitsTableProps) {
  return (
    <div className="table-scroll">
      <table className="table">
        <thead>
          <tr>
            {COLUMNS.map((label) => (
              <th key={label} scope="col">
                {label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {isLoading && <SkeletonRows />}

          {!isLoading && error && (
            <tr>
              <td colSpan={COLUMNS.length}>
                <div className="state">
                  <div className="state__icon">
                    <AlertIcon size={21} />
                  </div>
                  <p className="state__title">Ocurrió un problema</p>
                  <p className="state__text">{error}</p>
                </div>
              </td>
            </tr>
          )}

          {!isLoading && !error && benefits.length === 0 && (
            <tr>
              <td colSpan={COLUMNS.length}>
                <div className="state">
                  <div className="state__icon">
                    <InboxIcon size={21} />
                  </div>
                  <p className="state__title">
                    {hasActiveFilters ? "Sin resultados" : "Aún no hay registros"}
                  </p>
                  <p className="state__text">
                    {hasActiveFilters
                      ? "Ningún registro coincide con los filtros aplicados."
                      : "Carga una nómina en excel para comenzar a enviar la encuesta."}
                  </p>
                  {hasActiveFilters && (
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={onClearFilters}
                    >
                      Limpiar filtros
                    </button>
                  )}
                </div>
              </td>
            </tr>
          )}

          {!isLoading &&
            !error &&
            benefits.map((benefit: IBenefitItem) => (
              <tr key={benefit.benefitId}>
                <td className="cell--rut">{benefit.DNI}</td>
                <td className="cell--name">{benefit.name}</td>
                <td className="cell--phone">{formatPhone(benefit.phone)}</td>
                <td className="cell--date">
                  <DateTimeValue value={benefit.loadDate} />
                </td>
                <td className="cell--date">
                  {benefit.sendDate ? (
                    <DateTimeValue value={benefit.sendDate} />
                  ) : (
                    <span className="cell--empty">Pendiente</span>
                  )}
                </td>
                <td className="cell--date">
                  {benefit.answerDate ? (
                    <DateTimeValue value={benefit.answerDate} />
                  ) : (
                    <span className="cell--empty">&mdash;</span>
                  )}
                </td>
                <td>
                  <AnswerPill answer={benefit.answer} />
                </td>
                <td className="cell--file" title={benefit.file}>
                  {benefit.file}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

/** Muestra la fecha y la hora con una distinción visual clara entre ambas */
function DateTimeValue({ value }: { value: string | null }) {
  const { date, time } = formatDateParts(value);
  if (!date) return null;

  return (
    <span className="dt">
      <span className="dt__date">{date}</span>
      {time && <span className="dt__time">{time}</span>}
    </span>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, rowIndex) => (
        <tr className="skeleton-row" key={rowIndex}>
          {COLUMNS.map((label, cellIndex) => (
            <td key={label}>
              <div
                className="skeleton"
                style={{ width: `${[70, 90, 80, 85, 85, 85, 55, 75][cellIndex]}%` }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
